import { useSyncedRef } from "@react-hookz/web";
import { useCallback, useRef } from "react";
import type { YouTubePlayer } from "youtube-player/dist/types";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { useIntersection } from "scmp-app/lib/hooks";
type UseIntersectionHandlerParameters = {
  adsManager: OrUndefined<google.ima.AdsManager>;
  isAdPlaying: boolean;
  isAdStarted: boolean;
  isManualPlay: boolean;
  player: OrUndefined<YouTubePlayer>;
};

export const useIntersectionHandler = ({
  adsManager,
  isAdPlaying,
  isAdStarted,
  isManualPlay,
  player,
}: UseIntersectionHandlerParameters) => {
  const latestIsAdStartedReference = useSyncedRef(isAdStarted);
  const latestIsAdPlayingReference = useSyncedRef(isAdPlaying);

  const isFirstIntersection = useRef(true);

  const { captureIntersectionElement, intersection } = useIntersection({
    threshold: 0.5,
  });
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));

  const handleIsIntersecting = useCallback(() => {
    if (latestIsAdStartedReference.current) {
      adsManager?.resume();
    }

    // Desktop up will play video for auto play mode set, and manual play mode with auto play disabled
    if (!latestIsAdStartedReference.current && isDesktopUp && !isManualPlay) {
      if (isFirstIntersection.current) {
        // First time intersecting, the auto play will be done by YouTube player itself, so we set the flag to false and return.
        isFirstIntersection.current = false;
        return;
      }

      try {
        if (player) {
          void player?.playVideo();
        }
      } catch (error) {
        console.error("handleIsIntersecting playVideo", error);
      }
    }
  }, [adsManager, isDesktopUp, latestIsAdStartedReference, player, isManualPlay]);

  const handleIsNotIntersecting = useCallback(() => {
    if (latestIsAdStartedReference.current && latestIsAdPlayingReference.current) {
      adsManager?.pause();
    }
    try {
      void player?.pauseVideo();
    } catch (error) {
      console.error("handleIsNotIntersecting pauseVideo", error);
    }
  }, [adsManager, latestIsAdPlayingReference, latestIsAdStartedReference, player]);

  return {
    captureIntersectionElement,
    handleIsIntersecting,
    handleIsNotIntersecting,
    intersection,
  };
};
